// aifab - Dashboard
import { useEffect, useRef } from 'react';
import { useApi } from '../hooks/useApi';
import { StatusBadge } from '../components/common';

// ── Animated count-up number ─────────────────────────────────────────────────
function CountUp({ value }) {
  const ref = useRef(null);
  useEffect(() => {
    if (value == null || ref.current == null) return;
    const target = Number(value);
    if (isNaN(target)) { ref.current.textContent = value; return; }
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / 700, 1);
      const ease = 1 - Math.pow(1 - p, 3); // ease-out cubic
      ref.current.textContent = Math.floor(ease * target);
      if (p < 1) requestAnimationFrame(step);
      else ref.current.textContent = target.toLocaleString();
    };
    requestAnimationFrame(step);
  }, [value]);
  return <span ref={ref}>{value ?? '–'}</span>;
}

// ── Monoline stat icons (brand-consistent, no emoji) ────────────────────────
const ICON_PATHS = {
  folder:  'M3 7a2 2 0 0 1 2-2h4.2l1.8 2H19a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z',
  play:    'M8 5.5v13l10-6.5-10-6.5z',
  bolt:    'M13 3 6 13.5h4.5L10 21l7-10.5h-4.5L13 3z',
  chip:    'M7 7h10v10H7z M10 10h4v4h-4z M9 4v3M15 4v3M9 17v3M15 17v3M4 9h3M4 15h3M17 9h3M17 15h3',
  trophy:  'M8 5h8v3.5a4 4 0 0 1-8 0V5z M8 6.5H5.6v.7A2.6 2.6 0 0 0 8 9.8 M16 6.5h2.4v.7A2.6 2.6 0 0 1 16 9.8 M10 18h4 M12 12.5V18',
  server:  'M4 5.5h16v5H4z M4 13.5h16v5H4z M7 8h.01M7 16h.01',
};

function StatIcon({ name }) {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none"
      stroke="#E8470A" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d={ICON_PATHS[name]} />
    </svg>
  );
}

// ── Stat card (compact - six across in one line) ─────────────────────────────
function StatCard({ label, value, sub, icon }) {
  return (
    <div style={{
      background: '#FFFFFF',
      border: '1px solid #E4EBF5',
      borderRadius: 10,
      padding: '12px 14px 11px',
      minWidth: 0,
      boxShadow: '0 1px 3px rgba(13,27,46,.05)',
      transition: 'box-shadow .2s, transform .2s',
    }}
    onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(13,27,46,.10)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
    onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(13,27,46,.05)'; e.currentTarget.style.transform = 'none'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, gap: 6 }}>
        <div style={{
          fontSize: 9, fontWeight: 700, letterSpacing: '.08em',
          textTransform: 'uppercase', color: '#94A3B8',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{label}</div>
        {icon && (
          <div style={{
            width: 24, height: 24, borderRadius: 6,
            background: 'rgba(232,71,10,.07)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}><StatIcon name={icon} /></div>
        )}
      </div>

      <div style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 24, fontWeight: 400,
        color: '#0D1B2E', lineHeight: 1,
        letterSpacing: '-.02em',
      }}>
        <CountUp value={value} />
      </div>

      <div style={{ fontSize: 9.5, color: '#94A3B8', marginTop: 5, letterSpacing: '.02em', minHeight: 12,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {sub || '\u00A0'}
      </div>
    </div>
  );
}

// ── Server capacity bar ──────────────────────────────────────────────────────
function CapacityBar({ available, busy, total }) {
  const pct = total > 0 ? Math.round((busy / total) * 100) : 0;
  const color = pct > 75 ? '#DC2626' : pct > 40 ? '#D97706' : '#059669';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Bar */}
      <div style={{ height: 6, background: '#EEF2FA', borderRadius: 99, overflow: 'hidden' }}>
        <div style={{
          height: '100%', width: `${pct}%`,
          background: color,
          borderRadius: 99,
          transition: 'width .6s ease',
          minWidth: pct > 0 ? 6 : 0,
        }} />
      </div>

      {/* Stats row */}
      <div style={{ display: 'flex', gap: 6 }}>
        {[
          { label: 'Available', val: available, color: '#059669', bg: 'rgba(5,150,105,.07)', bd: 'rgba(5,150,105,.15)' },
          { label: 'Busy',      val: busy,      color: '#D97706', bg: 'rgba(217,119,6,.07)',  bd: 'rgba(217,119,6,.15)'  },
          { label: 'Total',     val: total,     color: '#64748B', bg: '#F7F9FC',              bd: '#E4EBF5'               },
        ].map(({ label, val, color, bg, bd }) => (
          <div key={label} style={{
            flex: 1, textAlign: 'center',
            background: bg, border: `1px solid ${bd}`,
            borderRadius: 8, padding: '10px 4px',
          }}>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 22, fontWeight: 400,
              color, lineHeight: 1, marginBottom: 4,
            }}>{val}</div>
            <div style={{
              fontSize: 9, color: '#94A3B8',
              textTransform: 'uppercase', letterSpacing: '.08em',
            }}>{label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Compact run row (widget style, not a table) ──────────────────────────────
const RUN_DOT = {
  COMPLETED: '#059669', RUNNING: '#D97706', TRAINING: '#D97706',
  QUEUED: '#2563EB', DISPATCHED: '#2563EB', FAILED: '#DC2626', CANCELLED: '#94A3B8',
};

function RunRowCompact({ run, last }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '10px 16px',
      borderBottom: last ? 'none' : '1px solid #F0F4F8',
      transition: 'background .12s',
    }}
    onMouseEnter={e => e.currentTarget.style.background = '#F7F9FC'}
    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      <span title={run.status} style={{
        width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
        background: RUN_DOT[run.status] || '#CBD5E1',
      }} />
      <span style={{
        fontFamily: "'IBM Plex Mono',monospace",
        fontSize: 12, color: '#E8470A', flexShrink: 0, width: 34,
      }}>#{run.run_number}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 12, fontWeight: 500, color: '#0D1B2E',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{run.project_name}</div>
        {run.run_label && (
          <div style={{
            fontSize: 10, color: '#94A3B8',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>{run.run_label}</div>
        )}
      </div>
      <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 11.5, flexShrink: 0 }}>
        {run.f1_score != null
          ? <span style={{ color: '#059669' }}>{(run.f1_score * 100).toFixed(1)}%</span>
          : <span style={{ color: '#CBD5E1' }}>—</span>}
      </span>
    </div>
  );
}

// ── Card section header with optional "View all" action ─────────────────────
function CardHead({ title, count, unit, onViewAll }) {
  return (
    <div style={{
      padding: '13px 16px', borderBottom: '1px solid #EEF2FA',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#0D1B2E' }}>{title}</span>
        {count != null && (
          <span style={{ fontSize: 10, color: '#94A3B8', fontWeight: 500 }}>
            {count} {unit}{count !== 1 ? 's' : ''}
          </span>
        )}
      </div>
      {onViewAll && (
        <button onClick={onViewAll} style={{
          background: 'none', border: 'none', padding: 0,
          fontSize: 11.5, fontWeight: 600, color: '#E8470A',
          fontFamily: 'inherit',
        }}>
          View all →
        </button>
      )}
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────
export default function Dashboard({ onNavigate }) {
  const { data, loading, reload } = useApi('/dashboard/stats');

  const s    = data?.stats ?? {};
  const proj = data?.recent_projects ?? [];
  const runs = data?.recent_runs ?? [];

  if (loading) return (
    <div className="page">
      <div className="page-hd"><div style={{ width:160, height:20, background:'#E8EEF6', borderRadius:6 }}/></div>
      {/* Skeleton stat cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:24 }}>
        {[...Array(4)].map((_,i) => (
          <div key={i} style={{ background:'#FFFFFF', border:'1px solid #E4EBF5',
            borderRadius:10, padding:'20px 24px', height:100,
            background:'linear-gradient(90deg,#FFFFFF 25%,#E8EEF6 50%,#FFFFFF 75%)',
            backgroundSize:'200% 100%', animation:'shimmer 1.4s infinite' }} />
        ))}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16 }}>
        {[...Array(4)].map((_,i) => (
          <div key={i} style={{ background:'#FFFFFF', border:'1px solid #E4EBF5',
            borderRadius:10, padding:'20px 24px', height:100,
            background:'linear-gradient(90deg,#FFFFFF 25%,#E8EEF6 50%,#FFFFFF 75%)',
            backgroundSize:'200% 100%', animation:'shimmer 1.4s infinite' }} />
        ))}
      </div>
    </div>
  );

  const now = new Date();
  const dateStr = now.toLocaleDateString('en-GB', { weekday:'long', day:'numeric', month:'long' });

  return (
    <div className="page">

      {/* ── Page header ──────────────────────────────────────────────────── */}
      <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'space-between', marginBottom:24 }}>
        <div>
          <h2 style={{ fontSize:24, fontWeight:700, color:'#0D1B2E', letterSpacing:'-.02em', lineHeight:1 }}>
            Overview
          </h2>
          <div style={{ fontSize:12, color:'#94A3B8', marginTop:4 }}>{dateStr}</div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={reload} title="Refresh stats"
          style={{ gap:5 }}>
          <span style={{ fontSize:13 }}>↻</span> Refresh
        </button>
      </div>

      {/* ── Stat cards - six across, one line ────────────────────────────── */}
      <div data-tour="dash-stats" className="dash-stats">
        <StatCard label="Projects"       value={s.projects}       icon="folder" />
        <StatCard label="Total Runs"     value={s.runs_total}     icon="play" />
        <StatCard label="Active Runs"    value={s.runs_active}    icon="bolt"
          sub={s.runs_active > 0 ? 'currently training' : 'none running'} />
        <StatCard label="Models Trained" value={s.models_trained} icon="chip" />
        <StatCard label="Champions"      value={s.champions}      icon="trophy" />
        <StatCard label="Servers Online" value={s.servers_online} icon="server"
          sub={s.servers_busy > 0 ? `${s.servers_busy} busy` : 'all available'} />
      </div>

      {/* ── Two-column content: Projects primary, ops rail right ─────────── */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 336px', gap:18, alignItems:'start' }}>

        {/* Left: Recent Projects (primary) */}
        <div data-tour="dash-projects" style={{
          background:'#FFFFFF', border:'1px solid #E4EBF5',
          borderRadius:12, overflow:'hidden',
          boxShadow:'0 1px 3px rgba(13,27,46,.05)',
        }}>
          <CardHead title="Recent Projects" count={proj.length} unit="project"
            onViewAll={onNavigate ? () => onNavigate('projects') : undefined} />

          {proj.length === 0 ? (
            <div style={{ padding:'40px 20px', textAlign:'center', color:'#94A3B8' }}>
              <div style={{ fontSize:28, marginBottom:10, opacity:.3 }}>📁</div>
              <div style={{ fontSize:13, fontWeight:500, color:'#475569' }}>No projects yet</div>
              <div style={{ fontSize:11, marginTop:4 }}>Create your first project to get started</div>
            </div>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ background:'#F7F9FC' }}>
                  {['Project','Client','Type','Status'].map(h => (
                    <th key={h} style={{
                      padding:'9px 16px', textAlign:'left',
                      fontSize:10, fontWeight:700, letterSpacing:'.08em',
                      textTransform:'uppercase', color:'#64748B',
                      borderBottom:'1px solid #E4EBF5',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {proj.map((p, i) => (
                  <tr key={p.id} style={{ borderBottom: i < proj.length-1 ? '1px solid #F0F4F8' : 'none' }}
                    onMouseEnter={e => e.currentTarget.style.background='#F7F9FC'}
                    onMouseLeave={e => e.currentTarget.style.background='transparent'}
                  >
                    <td style={{ padding:'11px 16px' }}>
                      <div style={{ fontSize:13, fontWeight:600, color:'#0D1B2E' }}>{p.project_name}</div>
                    </td>
                    <td style={{ padding:'11px 16px', fontSize:12, color:'#64748B' }}>{p.client_name}</td>
                    <td style={{ padding:'11px 16px' }}>
                      <span style={{
                        fontSize:10, fontWeight:600, padding:'2px 7px',
                        borderRadius:4, letterSpacing:'.04em',
                        background:'#EEF2FA', color:'#475569',
                      }}>{p.model_type}</span>
                    </td>
                    <td style={{ padding:'11px 16px' }}><StatusBadge code={p.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Right rail: capacity + compact runs widget */}
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

          {/* Server Capacity */}
          <div data-tour="dash-servers" style={{
            background:'#FFFFFF', border:'1px solid #E4EBF5',
            borderRadius:12, padding:'16px 18px',
            boxShadow:'0 1px 3px rgba(13,27,46,.05)',
          }}>
            <div style={{
              fontSize:13, fontWeight:600, color:'#0D1B2E',
              marginBottom:14,
            }}>Server Capacity</div>

            {s.servers_total === 0 ? (
              <div style={{ fontSize:12, color:'#94A3B8', padding:'8px 0' }}>
                No servers registered.
              </div>
            ) : (
              <CapacityBar
                available={Math.max(0,(s.servers_total||0)-(s.servers_busy||0))}
                busy={s.servers_busy||0}
                total={s.servers_total||0}
              />
            )}
          </div>

          {/* Recent Runs - compact widget; the Runs page is the full view */}
          <div data-tour="dash-runs" style={{
            background:'#FFFFFF', border:'1px solid #E4EBF5',
            borderRadius:12, overflow:'hidden',
            boxShadow:'0 1px 3px rgba(13,27,46,.05)',
          }}>
            <CardHead title="Recent Runs" count={runs.length} unit="run"
              onViewAll={onNavigate ? () => onNavigate('runs') : undefined} />

            {runs.length === 0 ? (
              <div style={{ padding:'28px 18px', textAlign:'center', color:'#94A3B8' }}>
                <div style={{ fontSize:22, marginBottom:8, opacity:.3 }}>🚀</div>
                <div style={{ fontSize:12, fontWeight:500, color:'#475569' }}>No runs yet</div>
              </div>
            ) : (
              <div>
                {runs.map((r, i) => (
                  <RunRowCompact key={r.id} run={r} last={i === runs.length - 1} />
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
